import React, { useState, useEffect } from "react";
import {
  AiOutlineRotateLeft,
  AiOutlineRotateRight,
  AiOutlineDownload,
  AiOutlineClose,
} from "react-icons/ai";
import { RiErrorWarningLine } from "react-icons/ri";

export function buildPhotos(check) {
  const photos = [];

  if (check.idPhoto) {
    const matchedFaces = check?.inferences?.faceMatch?.FaceMatches || [];
    const unmatchedFaces = check?.inferences?.faceMatch?.UnmatchedFaces || [];
    photos.push({
      label: "Front of ID",
      src: check.idPhoto,
      detailSrc: check.idPhotoDetail,
      matchedFaces,
      unmatchedFaces,
    });
  }

  if (check.backIdPhoto) {
    photos.push({ label: "Back of ID", src: check.backIdPhoto });
  }

  if (check.userPhoto) {
    const photo = { label: "Selfie", src: check.userPhoto };
    const sourceBox =
      check?.inferences?.faceMatch?.SourceImageFace?.BoundingBox;
    if (sourceBox) {
      photo.boundingBox = sourceBox;
    }
    photos.push(photo);
  }

  if (check.proofOfAddress) {
    photos.push({ label: "Proof of address", src: check.proofOfAddress });
  }

  if (check.signature) {
    photos.push({ label: "Signature", src: check.signature });
  }

  return photos;
}

function Overlays({ photo }) {
  return (
    <>
      {photo.boundingBox && (
        <div
          className="real-id-order-widget__bbox real-id-order-widget__bbox--id"
          style={{
            height: `${photo.boundingBox.Height * 100}%`,
            width: `${photo.boundingBox.Width * 100}%`,
            top: `${photo.boundingBox.Top * 100}%`,
            left: `${photo.boundingBox.Left * 100}%`,
          }}
        />
      )}
      {photo.matchedFaces?.map((row, i) => (
        <div
          key={`m-${i}`}
          className="real-id-order-widget__bbox real-id-order-widget__bbox--match"
          style={{
            height: `${row.Face.BoundingBox.Height * 100}%`,
            width: `${row.Face.BoundingBox.Width * 100}%`,
            top: `${row.Face.BoundingBox.Top * 100}%`,
            left: `${row.Face.BoundingBox.Left * 100}%`,
          }}
        />
      ))}
      {photo.unmatchedFaces?.map((face, i) => (
        <React.Fragment key={`u-${i}`}>
          <span className="real-id-order-widget__mismatch-badge">
            <RiErrorWarningLine />
            Face doesn't match
          </span>
          <div
            className="real-id-order-widget__bbox real-id-order-widget__bbox--mismatch"
            style={{
              height: `${face.BoundingBox.Height * 100}%`,
              width: `${face.BoundingBox.Width * 100}%`,
              top: `${face.BoundingBox.Top * 100}%`,
              left: `${face.BoundingBox.Left * 100}%`,
            }}
          />
        </React.Fragment>
      ))}
    </>
  );
}

// Padding-as-percentage of the photo frame width so the rotated image's
// overhang doesn't bleed into the controls / label. Same math as the
// dashboard ImageViewer.
function rotationOverflowPercent(aspect, rotation) {
  if (!aspect || aspect <= 1) return 0;
  if (rotation % 180 === 0) return 0;
  return ((aspect - 1) / (2 * aspect)) * 100;
}

export function LightboxLite({
  photo,
  sandboxMode,
  initialRotation = 0,
  onClose,
}) {
  const [rotation, setRotation] = useState(initialRotation);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="real-id-order-widget__lightbox"
      onClick={onClose}
      role="dialog"
      aria-label={`${photo.label} full size`}
    >
      <button
        type="button"
        aria-label="Close"
        className="real-id-order-widget__lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <AiOutlineClose size={22} />
      </button>
      <div
        className="real-id-order-widget__lightbox-controls"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Rotate left"
          className="real-id-order-widget__rotate-btn"
          onClick={() => setRotation(rotation - 90)}
        >
          <AiOutlineRotateLeft size={20} />
        </button>
        <button
          type="button"
          aria-label="Rotate right"
          className="real-id-order-widget__rotate-btn"
          onClick={() => setRotation(rotation + 90)}
        >
          <AiOutlineRotateRight size={20} />
        </button>
      </div>
      <div
        className="real-id-order-widget__lightbox-frame"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        <Overlays photo={photo} />
        {sandboxMode && (
          <div className="real-id-order-widget__sandbox-banner">Test Mode</div>
        )}
        <img
          className="real-id-order-widget__lightbox-img"
          src={photo.detailSrc || photo.src}
          alt={photo.label}
        />
      </div>
    </div>
  );
}

export default function ImagePreviewLite({
  photo,
  sandboxMode,
  onRequestDownload,
}) {
  const [rotation, setRotation] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgAspect, setImgAspect] = useState(null);

  const overflow = rotationOverflowPercent(imgAspect, rotation);
  const frameWrapperStyle =
    overflow > 0
      ? { paddingTop: `${overflow}%`, paddingBottom: `${overflow}%` }
      : undefined;

  return (
    <div className="real-id-order-widget__photo">
      <span className="real-id-order-widget__photo-label">{photo.label}</span>
      <div className="real-id-order-widget__photo-controls">
        <button
          type="button"
          aria-label="Download photo"
          className="real-id-order-widget__rotate-btn"
          onClick={() => onRequestDownload(photo)}
        >
          <AiOutlineDownload size={18} />
        </button>
        <button
          type="button"
          aria-label="Rotate left"
          className="real-id-order-widget__rotate-btn"
          onClick={() => setRotation(rotation - 90)}
        >
          <AiOutlineRotateLeft size={18} />
        </button>
        <button
          type="button"
          aria-label="Rotate right"
          className="real-id-order-widget__rotate-btn"
          onClick={() => setRotation(rotation + 90)}
        >
          <AiOutlineRotateRight size={18} />
        </button>
      </div>
      <div style={frameWrapperStyle}>
        <button
          type="button"
          className="real-id-order-widget__photo-frame"
          style={{ transform: `rotate(${rotation}deg)` }}
          onClick={() => setLightboxOpen(true)}
          aria-label={`Open ${photo.label} full size`}
        >
          <Overlays photo={photo} />
          {sandboxMode && (
            <div className="real-id-order-widget__sandbox-banner">Test Mode</div>
          )}
          <img
            className="real-id-order-widget__photo-img"
            src={photo.src}
            alt={photo.label}
            onLoad={(e) =>
              setImgAspect(
                e.currentTarget.naturalWidth / e.currentTarget.naturalHeight
              )
            }
          />
        </button>
      </div>
      {lightboxOpen && (
        <LightboxLite
          photo={photo}
          sandboxMode={sandboxMode}
          initialRotation={rotation}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
